import {uniq} from 'es-toolkit'
import {toString} from 'es-toolkit/compat'

import * as priceApi from '../../../services/api/price'
import {
  PossiblyMixedValue,
  isMixedValue,
  percentageStringToFactorMax1,
  toNumberValue,
  toPossiblyMixedValue,
  toStringValue,
} from '../../utils/form'

import {Price, PriceFormula, PricingTab, Provider} from '../../../types'

import {
  MixedFormData,
  PricingType,
  SingleFormData,
  VolumePricingType,
  WeightPricingType,
} from './form'

const createStaggeredPriceToRequest =
  (thresholdKey: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (priceValue: any): Array<{[key: string]: number}> | number | null =>
    Array.isArray(priceValue)
      ? priceValue.map((item: {[key: string]: string}) => ({
          [thresholdKey]: toNumberValue(item[thresholdKey]) as number,
          price: toNumberValue(item.price) as number,
        }))
      : toNumberValue(priceValue)

// FormData Pricing to priceApi Pricing
const toRequestVolumePrice = createStaggeredPriceToRequest('volume')
const toRequestWeightPrice = createStaggeredPriceToRequest('weight')

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formDataTransforms: Record<string, (value: any) => any> = {
  formula: formula => formula,
  name: name => name,
  basePrice: toNumberValue,
  baseUnitPrice: toNumberValue,
  boundingBoxPrice: toNumberValue,
  density: toNumberValue,
  minimumPrice: toNumberValue,
  minimumUnitPrice: toNumberValue,
  quantityDiscount: toNumberValue,
  quantityDiscountCap: percentageStringToFactorMax1,
  surfacePrice: toNumberValue,
  volumePrice: toRequestVolumePrice,
  weightPrice: toRequestWeightPrice,
  useVolumeWithSupport: value => value,
  machiningCostPerHour: toNumberValue,
  machiningMaterialRemovalRate: toNumberValue,
  finishingMaterialRemovalRate: toNumberValue,
  cncAxisTier: value => value,
  machineRefixtureTimePerSetup: toNumberValue,
  camProgrammingFeePerSetup: toNumberValue,
}

export const formDataToPriceRequest = (
  formData: SingleFormData,
  provider: Provider
): priceApi.PriceRequest => ({
  formula: formData.formula,
  name: formDataTransforms.name(formData.name),
  basePrice: formDataTransforms.basePrice(formData.basePrice),
  baseUnitPrice: formDataTransforms.baseUnitPrice(formData.baseUnitPrice),
  boundingBoxPrice: formDataTransforms.boundingBoxPrice(formData.boundingBoxPrice),
  density: formDataTransforms.density(formData.density),
  minimumPrice: formDataTransforms.minimumPrice(formData.minimumPrice),
  minimumUnitPrice: formDataTransforms.minimumUnitPrice(formData.minimumUnitPrice),
  quantityDiscount: formDataTransforms.quantityDiscount(formData.quantityDiscount),
  quantityDiscountCap: formDataTransforms.quantityDiscountCap(formData.quantityDiscountCap),
  surfacePrice: formDataTransforms.surfacePrice(formData.surfacePrice),
  volumePrice: formDataTransforms.volumePrice(formData.volumePrice),
  weightPrice: formDataTransforms.weightPrice(formData.weightPrice),
  useVolumeWithSupport: formDataTransforms.useVolumeWithSupport(formData.useVolumeWithSupport),
  machiningCostPerHour: formDataTransforms.machiningCostPerHour(formData.machiningCostPerHour),
  machiningMaterialRemovalRate: formDataTransforms.machiningMaterialRemovalRate(
    formData.machiningMaterialRemovalRate
  ),
  finishingMaterialRemovalRate: formDataTransforms.finishingMaterialRemovalRate(
    formData.finishingMaterialRemovalRate
  ),
  cncAxisTier: formDataTransforms.cncAxisTier(formData.cncAxisTier),
  machineRefixtureTimePerSetup: formDataTransforms.machineRefixtureTimePerSetup(
    formData.machineRefixtureTimePerSetup
  ),
  camProgrammingFeePerSetup: formDataTransforms.camProgrammingFeePerSetup(
    formData.camProgrammingFeePerSetup
  ),
  provider: provider.id,
})

export const formDataToPartialPriceRequest = (formData: Partial<SingleFormData>) => {
  const priceRequest: Partial<priceApi.PriceRequest> = {}

  Object.keys(formData).forEach(key => {
    const value = formData[key as keyof SingleFormData]
    const priceRequestTransform = formDataTransforms[key]
    if (!isMixedValue(value) && priceRequestTransform) {
      priceRequest[key as keyof priceApi.PriceRequest] = priceRequestTransform(value)
    }
  })

  return priceRequest
}

// priceApi Pricing to FormData Pricing
const createStaggeredPriceFromApi =
  (thresholdKey: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (value: any): any =>
    Array.isArray(value)
      ? value.map((item: {[key: string]: number}) => ({
          [thresholdKey]: toStringValue(item[thresholdKey]),
          price: toStringValue(item.price),
        }))
      : toStringValue(value)

const toVolumePrice = createStaggeredPriceFromApi('volume')
const toWeightPrice = createStaggeredPriceFromApi('weight')

const toPricingType = <T>(
  pricings: Price[],
  determineType: (pricing: Price) => T
): PossiblyMixedValue<T> => {
  const types = pricings.map(determineType)
  const isMultipleTypes = uniq(types).length > 1
  return isMultipleTypes ? {mixed: true, values: types} : types[0]
}

const toFormula = <T>(
  pricings: Price[],
  determineType: (pricing: Price) => T
): PossiblyMixedValue<T> => {
  const types = pricings.map(determineType)
  const isMultipleTypes = uniq(types).length > 1
  return isMultipleTypes ? {mixed: true, values: types} : types[0]
}

export const pricingsToFormData = (pricings: Price[]): MixedFormData => ({
  formula: toFormula<PriceFormula>(pricings, (pricing: Price) => pricing.formula),
  name: toPossiblyMixedValue(
    pricings.map(pricing => pricing.name),
    toString
  ),
  basePrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.basePrice),
    toStringValue
  ),
  baseUnitPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.baseUnitPrice),
    toStringValue
  ),
  boundingBoxPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.boundingBoxPrice),
    toStringValue
  ),
  density: toPossiblyMixedValue(
    pricings.map(pricing => pricing.density),
    toStringValue
  ),
  minimumPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.minimumPrice),
    toStringValue
  ),
  minimumUnitPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.minimumUnitPrice),
    toStringValue
  ),
  quantityDiscount: toPossiblyMixedValue(
    pricings.map(pricing => pricing.quantityDiscount),
    toStringValue
  ),
  quantityDiscountCap: toPossiblyMixedValue(
    pricings.map(pricing =>
      pricing.quantityDiscountCap ? pricing.quantityDiscountCap * 100 : null
    ),
    toStringValue
  ),
  surfacePrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.surfacePrice),
    toStringValue
  ),
  volumePrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.volumePrice),
    toVolumePrice
  ),
  weightPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.weightPrice),
    toWeightPrice
  ),
  pricingType: toPricingType<PricingType>(pricings, (pricing: Price) =>
    pricing.weightPrice ? PricingType.WEIGHT : PricingType.VOLUME
  ),
  volumePricingType: toPricingType<VolumePricingType>(pricings, (pricing: Price) =>
    Array.isArray(pricing.volumePrice) ? VolumePricingType.STAGGERED : VolumePricingType.SINGLE
  ),
  weightPricingType: toPricingType<WeightPricingType>(pricings, (pricing: Price) =>
    Array.isArray(pricing.weightPrice) ? WeightPricingType.STAGGERED : WeightPricingType.SINGLE
  ),
  useVolumeWithSupport: toPossiblyMixedValue(
    pricings.map(pricing => pricing.useVolumeWithSupport),
    Boolean
  ),
  machiningCostPerHour: toPossiblyMixedValue(
    pricings.map(pricing => pricing.machiningCostPerHour),
    toStringValue
  ),
  machiningMaterialRemovalRate: toPossiblyMixedValue(
    pricings.map(pricing => pricing.machiningMaterialRemovalRate),
    toStringValue
  ),
  finishingMaterialRemovalRate: toPossiblyMixedValue(
    pricings.map(pricing => pricing.finishingMaterialRemovalRate),
    toStringValue
  ),
  cncAxisTier: toPossiblyMixedValue(
    pricings.map(pricing => pricing.cncAxisTier),
    v => v
  ),
  machineRefixtureTimePerSetup: toPossiblyMixedValue(
    pricings.map(pricing => pricing.machineRefixtureTimePerSetup),
    toStringValue
  ),
  camProgrammingFeePerSetup: toPossiblyMixedValue(
    pricings.map(pricing => pricing.camProgrammingFeePerSetup),
    toStringValue
  ),
})

export const pricingsToSingleFormData = (pricing: Price): SingleFormData => ({
  formula: pricing.formula,
  name: pricing.name,
  basePrice: toStringValue(pricing.basePrice),
  baseUnitPrice: toStringValue(pricing.baseUnitPrice),
  boundingBoxPrice: toStringValue(pricing.boundingBoxPrice),
  density: toStringValue(pricing.density),
  minimumPrice: toStringValue(pricing.minimumPrice),
  minimumUnitPrice: toStringValue(pricing.minimumUnitPrice),
  quantityDiscount: toStringValue(pricing.quantityDiscount),
  quantityDiscountCap: toStringValue(
    pricing.quantityDiscountCap ? pricing.quantityDiscountCap * 100 : null
  ),
  surfacePrice: toStringValue(pricing.surfacePrice),
  volumePrice: toVolumePrice(pricing.volumePrice),
  weightPrice: toWeightPrice(pricing.weightPrice),
  pricingType: pricing.weightPrice ? PricingType.WEIGHT : PricingType.VOLUME,
  volumePricingType: Array.isArray(pricing.volumePrice)
    ? VolumePricingType.STAGGERED
    : VolumePricingType.SINGLE,
  weightPricingType: Array.isArray(pricing.weightPrice)
    ? WeightPricingType.STAGGERED
    : WeightPricingType.SINGLE,
  useVolumeWithSupport: pricing.useVolumeWithSupport,
  machiningCostPerHour: toStringValue(pricing.machiningCostPerHour),
  machiningMaterialRemovalRate: toStringValue(pricing.machiningMaterialRemovalRate),
  finishingMaterialRemovalRate: toStringValue(pricing.finishingMaterialRemovalRate),
  cncAxisTier: pricing.cncAxisTier,
  machineRefixtureTimePerSetup: toStringValue(pricing.machineRefixtureTimePerSetup),
  camProgrammingFeePerSetup: toStringValue(pricing.camProgrammingFeePerSetup),
})

export const priceFormulaOptionsMap = {
  [PriceFormula.THREE_D_PRINTING]: {
    label: '3D Printing',
    value: PriceFormula.THREE_D_PRINTING,
    tabKey: PricingTab.THREE_D_PRINTING,
  },
  [PriceFormula.CNC_MACHINING]: {
    label: 'CNC Machining',
    value: PriceFormula.CNC_MACHINING,
    tabKey: PricingTab.CNC_MACHINING,
  },
}

export const getFormulaLabel = (formula: PriceFormula) => priceFormulaOptionsMap[formula]?.label
